import { FilePlus, Loader2Icon, Trash } from "lucide-react"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { UpdateProfileRequest, UpdateProfileResponse } from "../../types/user.type"
import { useUpdateProfileMutation, useUpdateProfilePictureMutation } from "../../redux/api/profile.api"
import { toast } from "react-toastify"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getToken } from "../../redux/api/Auth"
import axios from "axios"
import { setCredentials, setUser } from "../../redux/slices/authSlice"
import { BACKEND_URL } from "../../utils/env.config"
import { DetectOSAndBrowser } from "../../redux/api/DetectBroweser"

interface Props {
  setActiveTab: React.Dispatch<React.SetStateAction<string>>
}
export default function ProfileEdit({ setActiveTab }: Props) {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.auth.user)
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null)
  const [profileImage, setProfileImage] = useState<string>(
    "https://laravelui.spruko.com/zynix/build/assets/images/faces/2.jpg"
  )


  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { register, handleSubmit, formState: { errors, isDirty }, reset } = useForm<UpdateProfileRequest>({
    defaultValues: {
      FirstName: user?.firstName || "",
      MiddleName: user?.middleName || "",
      LastName: user?.lastName || "",
      PrimaryPhone: user?.primaryPhone || "",
      WebSiteUrl: user?.webSiteUrl || "",
      LinkedInProfileUrl: user?.linkedInProfileUrl || "",
      GitHubProfileUrl: user?.gitHubProfileUrl || "",
    },
  });

  useEffect(() => {
    if (user?.profilePicturePath) {
      setProfileImage(user?.profilePicturePath)
    }
  }
    , [user?.profilePicturePath])

  const [profileChanged, setProfileChanged] = useState(false)

  const [updateProfile, { error }] = useUpdateProfileMutation();
  const [updateProfilePicture] = useUpdateProfilePictureMutation();

  const onSubmit = async (data: UpdateProfileRequest) => {
    setIsSubmitting(true)
    try {
      const response = await updateProfile(data).unwrap();
      localStorage.setItem('user', JSON.stringify(response));
      dispatch(setUser(response))
      toast.success("Profile updated successfully")
      setIsSubmitting(false)
      setActiveTab("about")
    } catch (error) {
      toast.error("Error occurred while updating profile")
      setIsSubmitting(false)
    }
  }
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          setProfileImage(e.target.result as string)
          setProfileChanged(true)
        }
      }
      reader.readAsDataURL(file)
      setProfilePictureFile(file)
    }
  }



  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveImage = () => {
    setProfileImage("/placeholder.svg?height=300&width=300")
  }

  const handleUpdateProfilePicture = async () => {
    setIsSubmitting(true)
    try {
      const formData = new FormData();
      if (!profilePictureFile) return;
      formData.append("ProfilePictureFile", profilePictureFile);
      const response = await axios.post(
        `${BACKEND_URL}users/UpdateProfilePicture`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${getToken()}`,
            "Client-OS": DetectOSAndBrowser().OS,
            "Client-Browser": DetectOSAndBrowser().Browser,
          },
          withCredentials: true,
        }
      );
      localStorage.setItem('user', JSON.stringify(response.data));
      dispatch(setUser(response.data))
      toast.success("Profile picture updated successfully")
      setProfileChanged(false)
      setIsSubmitting(false)
    } catch (error) {
      toast.error("Error occurred while updating profile picture")
      setIsSubmitting(false)
    }
  }

  return (
    <div className="p-6  dark:text-slate-50 ">
      <div className="flex w-full justify-between items-center mb-6 ">
        <h1 className="sm:text-xl text-base font-semibold">Account Settings :</h1>
        {isDirty && <div>
          <button disabled={isSubmitting} onClick={() => reset()} className="bg-[#3FBBA3] text-white px-3 py-1.5 rounded text-sm">Restore <span className="hidden sm:inline">Changes</span> </button>
        </div>}
      </div>
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            <img
              src={profileImage || "/placeholder.svg"}
              alt="Profile"
              className="w-16 h-16 rounded-md object-cover"
            />
            <div>
              <p>Profile picture</p>
              <div className="flex gap-2">
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/jpeg,image/png,image/gif"
                    className="hidden"
                  />
                  <div className="flex sm:items-center gap-1 flex-col sm:flex-row items-stretch">

                    <button
                      disabled={isSubmitting}
                      onClick={triggerFileInput}
                      className="bg-[#3FBBA3] text-white px-2 py-1 rounded text-xs flex items-center gap-1 disabled:cursor-not-allowed"
                    >
                      <FilePlus size={16} /> {/* Add the file plus icon */}
                      Change Image
                    </button>
                    <button
                      disabled={isSubmitting || !profileChanged}
                      onClick={handleRemoveImage}
                      className="bg-red-500 text-white px-2 py-1 rounded text-xs disabled:cursor-not-allowed"
                    >
                      Remove
                    </button>
                    {
                      profileChanged &&
                      <button
                        disabled={isSubmitting}
                        onClick={handleUpdateProfilePicture}
                        className={`bg-accent-500/95 hover:bg-accent-500 text-white px-2 py-1 rounded text-xs disabled:cursor-not-allowed`}
                      >
                        Save
                      </button>
                    }
                  </div>
                </div>

              </div>

              <div className="text-xs text-gray-400 dark:text-gray-500">
                Use JPG, PNG, or GIF. Best size 300x300 pixels. Keep it under 5MB
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
          <div className="space-y-1">
            <label className="text-sm">First Name :</label>
            <input
              type="text"
              {...register("FirstName", { required: "First Name is required" })}
              placeholder="First Name"
              className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
            />
            {errors.FirstName && <p className="text-red-500 text-xs">{errors.FirstName.message}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm">Middle Name :</label>
            <input
              type="text"
              {...register("MiddleName", { required: "Middle Name is required" })}
              placeholder="Middle Name"
              className="w-full p-2 rounded border text-xs bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
            />
            {errors.MiddleName && <p className="text-red-500 text-xs">{errors.MiddleName.message}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm">Last Name :</label>
            <input
              type="text"
              {...register("LastName", { required: "Last Name is required" })}
              placeholder="Last Name"
              className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
            />
            {errors.LastName && <p className="text-red-500 text-xs">{errors.LastName.message}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm"> Phone Number:</label>
            <input
              type="text"
              {...register("PrimaryPhone", { required: "Phone Number is required" })}
              placeholder="Enter Number"
              className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
            />
            {errors.PrimaryPhone && <p className="text-red-500 text-xs">{errors.PrimaryPhone.message}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm">Web site url :</label>
            <input
              type="url"
              {...register("WebSiteUrl", {
                pattern: {
                  value: /^(ftp|http|https):\/\/[^ "]+$/,
                  message: "Invalid url"
                }
              })}
              placeholder="https://www.website.com"
              className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
            />
            {errors.WebSiteUrl && <p className="text-red-500 text-xs">{errors.WebSiteUrl.message}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm">Linkedin profile URL :</label>
            <input
              type="url"
              {...register("LinkedInProfileUrl", {
                pattern: {
                  value: /^(ftp|http|https):\/\/[^ "]+$/,
                  message: "Invalid url"
                }
              })}
              placeholder="https://www.linkedin.com/in/username"
              className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
            />
            {errors.LinkedInProfileUrl && <p className="text-red-500 text-xs">{errors.LinkedInProfileUrl.message}</p>}
          </div>

          <div className="space-y-1 md:col-span-2">
            <label className="text-sm">Github Profile URL :</label>
            <input
              type="url"
              {...register("GitHubProfileUrl", {
                pattern: {
                  value: /^(ftp|http|https):\/\/[^ "]+$/,
                  message: "Invalid url"
                }
              })}
              placeholder="https://www.github.com/username"
              className="w-full p-2 text-xs rounded border bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600"
            />
            {errors.GitHubProfileUrl && <p className="text-red-500 text-xs">{errors.GitHubProfileUrl.message}</p>}
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-4 mt-6">


          <div className="w-full md:w-auto">
            <button disabled={isSubmitting || !isDirty} className="bg-accent-500/95 hover:bg-accent-500 text-white px-4 text-sm py-2 rounded w-full md:w-auto disabled:opacity-80 disabled:cursor-not-allowed">
              {isDirty ? (isSubmitting ? <span className="flex items-center gap-1"><Loader2Icon className="h-4 w-4 text-white animate-spin" /> Saving...</span> : "Save Changes") : "No Changes Made"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
